import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { 
  AlertTriangle, 
  MessageSquare, 
  Clock,
  Eye,
  Activity,
  Shield,
  ExternalLink,
  Database,
  BarChart3,
  RefreshCw,
  Edit
} from 'lucide-react';
import { apiService } from '../services/api';
import './Dashboard.css';

// Utility function to format timestamps in US format (MM/DD/YYYY with AM/PM)
const formatAmericanTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  // Explicitly convert UTC to local time
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  
  return localDate.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Utility function to format current time (already local)
const formatCurrentTime = (date) => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const Dashboard = () => {
  const [timeRange] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewMessage, setReviewMessage] = useState(null);
  const [reviewComment, setReviewComment] = useState('');

  // Fetch statistics
  const { data: statsResponse, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useQuery(
    ['statistics', timeRange],
    apiService.getStatistics,
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
    }
  );

  // Extract stats from the response
  const stats = statsResponse?.data || null;

  // Fetch high-risk messages
  const { data: highRiskResponse, isLoading: messagesLoading, error: messagesError, refetch: refetchMessages } = useQuery(
    ['high-risk-messages'],
    () => apiService.getMessages({ risk_level: 'HIGH', per_page: 5 }),
    {
      refetchInterval: 15000, // Refresh every 15 seconds
      retry: 3,
    }
  );

  // Extract messages from the response
  const highRiskMessages = highRiskResponse?.messages || [];

  // Refresh function
  const handleRefresh = async () => {
    try {
      await Promise.all([refetchStats(), refetchMessages()]);
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
    }
  };

  const handleReviewClick = (message) => {
    setReviewMessage(message);
    setReviewComment(message.review_comment || '');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      alert('Please enter a review comment');
      return;
    }

    try {
      const response = await apiService.submitReview(reviewMessage.id, {
        review_comment: reviewComment.trim()
      });

      if (response.success) {
        alert('Review submitted successfully');
        setShowReviewModal(false);
        setReviewMessage(null);
        setReviewComment('');
        refetchMessages(); // Refresh the messages list
      }
    } catch (error) {
      alert('Failed to submit review');
      console.error('Review submission error:', error);
    }
  };

  // Fetch messages for the last 24 hours for hourly chart
  const { data: hourlyMessagesResponse, isLoading: hourlyLoading } = useQuery(
    ['hourly-messages'],
    () => apiService.getMessages({ per_page: 50 }), // Use a reasonable page size
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
    }
  );

  // Process hourly message data
  const processHourlyData = () => {
    const messages = hourlyMessagesResponse?.messages || [];
    const now = new Date();
    const hours = [];
    
    // Create array of last 24 hours in local time
    for (let i = 23; i >= 0; i--) {
      const hour = new Date(now);
      hour.setHours(now.getHours() - i, 0, 0, 0);
      hours.push({
        hour: hour.getHours(),
        timestamp: hour,
        count: 0,
        label: hour.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          hour12: true 
        })
      });
    }

    // Count messages for each hour
    messages.forEach(message => {
      // Try different possible timestamp field names
      const timestamp = message.timestamp || message.created_at || message.date || message.created_date;
      if (!timestamp) {
        return;
      }
      
      const messageTime = new Date(timestamp);
      
      // Skip if message time is invalid
      if (isNaN(messageTime.getTime())) {
        return;
      }
      
      // Convert UTC timestamp to local time by subtracting timezone offset
      const localMessageTime = new Date(messageTime.getTime() - (new Date().getTimezoneOffset() * 60000));
      
      // Find which hour bucket this message belongs to
      for (let i = 0; i < hours.length; i++) {
        const hourStart = hours[i].timestamp;
        const hourEnd = new Date(hourStart);
        hourEnd.setHours(hourStart.getHours() + 1);
        
        if (localMessageTime >= hourStart && localMessageTime < hourEnd) {
          hours[i].count++;
          break;
        }
      }
    });

    return hours;
  };

  const hourlyData = processHourlyData();

  // Fetch Merkle tree info
  const { data: merkleResponse, isLoading: merkleLoading } = useQuery(
    'merkle-root',
    apiService.getMerkleRoot,
    {
      refetchInterval: 60000, // Refresh every minute
      retry: 3,
    }
  );



  // Message type distribution
  const messageTypeData = stats?.message_type_distribution ? 
    Object.entries(stats.message_type_distribution)
      .filter(([type, count]) => count > 0) // Only show types with data
      .map(([type, count]) => ({
        name: type.replace('_', ' '),
        value: count,
      })) : [];

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle = null }) => (
    <div className="stat-card">
      <div className="stat-header">
        <Icon className={`stat-icon ${color}`} />
      </div>
      <div className="stat-content">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-title">{title}</p>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  const RiskBadge = ({ score }) => {
    const getRiskLevel = (score) => {
        if (score >= 80) return { level: 'CRITICAL', color: '#a35252' };
  if (score >= 70) return { level: 'HIGH', color: '#a35252' };
  if (score >= 50) return { level: 'MEDIUM', color: '#a36b52' };
  if (score >= 20) return { level: 'LOW', color: '#a3a352' };
  return { level: 'NONE', color: '#00a352' };
    };

    const { color } = getRiskLevel(score);
    return (
      <span className="risk-badge" style={{ backgroundColor: color }}>
        {score}
      </span>
    );
  };

  const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="empty-state">
      <Icon className="empty-state-icon" />
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );

  if (statsLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="dashboard-error">
        <AlertTriangle className="error-icon" />
        <h3>Error Loading Dashboard</h3>
        <p>Failed to load statistics. Please check your connection and try again.</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Compliance Dashboard</h1>
          <div className="header-controls">
          <div className="last-updated">
              <Clock className="clock-icon" />
              <span>Updated {formatCurrentTime(new Date())}</span>
            </div>
            <button 
              className="refresh-btn" 
              onClick={handleRefresh}
              disabled={statsLoading || messagesLoading}
              title="Refresh dashboard data"
            >
              <RefreshCw className={`refresh-icon ${statsLoading || messagesLoading ? 'spinning' : ''}`} size={16} />
              Refresh
            </button>
            
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          title="Total Messages"
          value={stats?.total_messages || 0}
          icon={MessageSquare}
          color="blue"
        />
        <StatCard
          title="High Risk Messages"
          value={stats?.high_risk_messages || 0}
          icon={AlertTriangle}
          color="red"
          subtitle={stats?.high_risk_percentage ? `${stats.high_risk_percentage}% of total` : null}
        />
        <StatCard
          title="Average Risk Score"
          value={stats?.average_risk_score ? stats.average_risk_score.toFixed(1) : '0.0'}
          icon={Activity}
          color="orange"
        />
        <StatCard
          title="Risk Distribution"
          value={Object.keys(stats?.risk_level_distribution || {}).length}
          icon={BarChart3}
          color="green"
          subtitle="Active risk levels"
        />
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-grid">
          {/* Messages Per Hour Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Messages Per Hour (Last 24 Hours)</h3>
            </div>
            <div className="chart-body">
              {hourlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="label" 
                      stroke="#808080"
                      fontSize={10}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      stroke="#808080"
                      fontSize={10}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #404040',
                        borderRadius: '4px',
                        color: '#e0e0e0',
                        fontSize: '12px',
                        padding: '8px 12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                      }}
                      labelStyle={{
                        color: '#e0e0e0',
                        fontWeight: '600',
                      }}
                      formatter={(value, name) => [value, 'Messages']}
                      labelFormatter={(label) => `Hour: ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#0052a3" 
                      strokeWidth={2}
                      dot={{ fill: '#0052a3', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#0052a3', strokeWidth: 2, fill: '#1a1a1a' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  icon={Clock}
                  title="No Hourly Data"
                  description="No messages in the last 24 hours"
                />
              )}
            </div>
          </div>

          {/* Message Type Distribution Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Message Type Distribution</h3>
            </div>
            <div className="chart-body">
              {messageTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={messageTypeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#808080"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#808080"
                      fontSize={12}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #404040',
                        borderRadius: '4px',
                        color: '#e0e0e0',
                        fontSize: '12px',
                        padding: '8px 12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                      }}
                      labelStyle={{
                        color: '#e0e0e0',
                        fontWeight: '600',
                      }}
                    />
                    <Bar dataKey="value" fill="#0066cc" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  icon={Database}
                  title="No Message Types"
                  description="No message type data available"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* High Risk Messages */}
      <div className="high-risk-section">
        <div className="section-header">
          <h2>Recent High Risk Messages</h2>
          <button className="btn btn-primary btn-sm" onClick={() => window.location.href = '/messages'}>
            <Eye className="btn-icon" />
            View All
          </button>
        </div>
        
        <div className="messages-table">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Message</th>
                <th>Risk Score</th>
                <th>Violations</th>
                <th>Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {messagesLoading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <div className="loading-spinner"></div>
                    Loading messages...
                  </td>
                </tr>
              ) : messagesError ? (
                <tr>
                  <td colSpan="6" className="text-center text-error">
                    Error loading messages
                  </td>
                </tr>
              ) : highRiskMessages.length > 0 ? (
                highRiskMessages.map((message) => (
                  <tr key={message.id}>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{message.user_name || message.user_id}</span>
                        <span className="channel-id">#{message.channel_id}</span>
                      </div>
                    </td>
                    <td>
                      <div className="message-content">
                        {message.message_text.length > 60 
                          ? `${message.message_text.substring(0, 60)}...` 
                          : message.message_text
                        }
                      </div>
                    </td>
                    <td>
                      <RiskBadge score={message.risk_score} />
                    </td>
                    <td>
                      <div className="violations">
                        {message.compliance_violations?.map((violation, index) => (
                          <span key={index} className="violation-tag">
                            {violation}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className="timestamp">
                        {formatAmericanTime(message.timestamp)}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button 
                          className={`btn btn-sm ${message.review_comment ? 'btn-warning' : 'btn-secondary'}`}
                          onClick={() => handleReviewClick(message)}
                          title={message.review_comment ? 'Edit Review' : 'Add Review'}
                        >
                          <Edit className="btn-icon" />
                        </button>
                        <button 
                          className={`btn btn-sm ${message.completion_id ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={message.completion_id ? async () => {
                            try {
                              const response = await apiService.getBlockchainCompletionExplorerUrl(message.completion_id);
                              if (response.explorer_url) {
                                window.open(response.explorer_url, '_blank');
                              }
                            } catch (error) {
                              console.error('Error getting blockchain URL:', error);
                            }
                          } : undefined}
                          disabled={!message.completion_id}
                          title={message.completion_id ? 'Trace on Blockchain' : 'No blockchain data available'}
                        >
                          <ExternalLink className="btn-icon" />
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => setSelectedMessage(message)}
                          title="View Details"
                        >
                          <Eye className="btn-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    <EmptyState
                      icon={Shield}
                      title="No High Risk Messages"
                      description="No high risk messages found in the system"
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Merkle Tree Info */}
      <div className="merkle-section">
        <div className="card">
          <div className="card-header">
            <h3>Merkle Tree Audit Trail</h3>
          </div>
          <div className="card-body">
                        {merkleLoading ? (
              <div className="loading-spinner"></div>
            ) : merkleResponse?.root_hash ? (
              <div className="merkle-info">
                <div className="merkle-item">
                  <strong>Root Hash:</strong>
                  <code className="merkle-hash">
                    {merkleResponse.root_hash}
                  </code>
                </div>
                <div className="merkle-item">
                  <strong>Last Updated:</strong>
                  <span>
                    {formatAmericanTime(merkleResponse.timestamp)}
                  </span>
                </div>
              </div>
            ) : (
              <EmptyState
                icon={Database}
                title="No Merkle Root"
                description="No blockchain audit trail available yet"
              />
            )}
          </div>
        </div>
      </div>

      {/* Message Details Modal */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Message Details</h3>
              <button 
                className="btn btn-sm"
                onClick={() => setSelectedMessage(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="message-detail-grid">
                <div className="detail-item">
                  <strong>User:</strong>
                  <span>{selectedMessage.user_name || selectedMessage.user_id}</span>
                </div>
                <div className="detail-item">
                  <strong>Channel:</strong>
                  <span>{selectedMessage.channel_name || selectedMessage.channel_id}</span>
                </div>
                <div className="detail-item">
                  <strong>Risk Score:</strong>
                  <RiskBadge score={selectedMessage.risk_score} />
                </div>
                <div className="detail-item">
                  <strong>Risk Level:</strong>
                  <span>{selectedMessage.risk_level}</span>
                </div>
                <div className="detail-item">
                  <strong>Message Type:</strong>
                  <span>{selectedMessage.message_type}</span>
                </div>
                <div className="detail-item">
                  <strong>Timestamp:</strong>
                  <span>{formatAmericanTime(selectedMessage.timestamp)}</span>
                </div>
                <div className="detail-item full-width">
                  <strong>Message:</strong>
                  <div className="full-message">
                    {selectedMessage.message_text}
                  </div>
                </div>
                {selectedMessage.compliance_violations && selectedMessage.compliance_violations.length > 0 && (
                  <div className="detail-item full-width">
                    <strong>Violations:</strong>
                    <div className="violations">
                      {selectedMessage.compliance_violations.map((violation, index) => (
                        <span key={index} className="violation-tag">
                          {violation}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedMessage.compliance_reasoning && (
                  <div className="detail-item full-width">
                    <strong>Analysis:</strong>
                    <div className="reasoning">
                      {selectedMessage.compliance_reasoning}
                    </div>
                  </div>
                )}
                {selectedMessage.completion_id && (
                  <div className="detail-item full-width">
                    <strong>Blockchain:</strong>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={async () => {
                        try {
                          const response = await apiService.getBlockchainCompletionExplorerUrl(selectedMessage.completion_id);
                          if (response.explorer_url) {
                            window.open(response.explorer_url, '_blank');
                          }
                        } catch (error) {
                          console.error('Error getting blockchain URL:', error);
                        }
                      }}
                    >
                      <ExternalLink className="btn-icon" />
                      Trace on Blockchain
                    </button>
                  </div>
                )}
                {selectedMessage.review_comment && (
                  <div className="detail-item full-width">
                    <strong>Review Comment:</strong>
                    <div className="review-comment">
                      {selectedMessage.review_comment}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && reviewMessage && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Message</h3>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => setShowReviewModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="review-form">
                <div className="message-preview">
                  <strong>Message:</strong>
                  <div className="message-text">{reviewMessage.message_text}</div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Review Comment</label>
                  <textarea
                    className="form-control"
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Enter your review comment explaining why you disagree with the AI analysis..."
                    rows={4}
                  />
                </div>

                <div className="quick-reasons">
                  <label className="form-label">Quick Reasons (click to add):</label>
                  <div className="quick-reason-buttons">
                    {[
                      "AI misinterpreted the context",
                      "Risk score too high for this message",
                      "No actual compliance violation",
                      "Message is standard business communication",
                      "False positive detection"
                    ].map((reason, index) => (
                      <button
                        key={index}
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setReviewComment(prev => prev + (prev ? '\n' : '') + reason)}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowReviewModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSubmitReview}
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 